import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown as ChevronDown, X } from '@phosphor-icons/react';
import { usePortalRoot } from '../../context/PortalContext';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import './ui-transforms.css';
import './multi-select.css';

/**
 * Multi-select dropdown component with checkboxes
 * @param {Object} props
 * @param {string} props.id - Element ID
 * @param {string} props.placeholder - Placeholder text when nothing selected
 * @param {Array<string>} props.options - Available options
 * @param {Array<string>} props.value - Selected values
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.className - Additional CSS classes
 * @param {Object} [props.counts] - Optional facet counts map { optionValue: number }
 * @param {Function} [props.getOptionLabel] - Maps an option value to its visible label
 *   (values stay untouched in `value`/`onChange`; #1417 — translated inventory values)
 */
export const MultiSelect = ({
  id,
  placeholder = 'Select...',
  options = [],
  value = [],
  onChange,
  className = '',
  counts = null,
  getOptionLabel = (option) => option,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const portalRoot = usePortalRoot();

  // Dynamic dropdown positioning via shared hook
  const handleCloseAll = useCallback(() => setIsOpen(false), []);
  const dropdownPosition = useDropdownPosition(isOpen, triggerRef, dropdownRef, {
    defaultMaxHeight: 240,
    onCloseAll: handleCloseAll,
  });

  // Close when another dropdown opens (coordination via CustomEvent)
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('dropdown-open', { detail: { id } }));
    }
  }, [isOpen, id]);

  useEffect(() => {
    const handleOtherOpen = (e) => {
      if (e.detail.id !== id) {
        setIsOpen(false);
      }
    };
    window.addEventListener('dropdown-open', handleOtherOpen);
    return () => window.removeEventListener('dropdown-open', handleOtherOpen);
  }, [id]);

  // Закрываем dropdown при клике вне компонента
  // Используем composedPath() для корректной работы в Shadow DOM
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current && !triggerRef.current) return;

      // Используем composedPath() для работы с Shadow DOM
      // event.target может быть retargeted на shadow host,
      // но composedPath() показывает полный путь включая Shadow DOM
      const path = event.composedPath ? event.composedPath() : [event.target];
      const isInsideTrigger = triggerRef.current && path.includes(triggerRef.current);
      const isInsideDropdown = dropdownRef.current && path.includes(dropdownRef.current);

      if (!isInsideTrigger && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const toggleOption = (option) => {
    const newValue = value.includes(option)
      ? value.filter(v => v !== option)
      : [...value, option];
    onChange(newValue);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return getOptionLabel(value[0]);
    if (value.length === 2) return value.map(getOptionLabel).join(', ');
    return `${value.length} selected`;
  };

  return (
    <div ref={triggerRef} className={`multi-select ${className}`.trim()}>
      {/* Trigger button */}
      <button
        id={id}
        type="button"
        disabled={options.length === 0}
        onClick={() => { if (options.length > 0) setIsOpen(!isOpen); }}
        onTouchEnd={(e) => {
          // Explicit touch handling to ensure proper dropdown behavior on mobile
          // This prevents issues where touch events might propagate incorrectly
          e.preventDefault();
          if (options.length > 0) setIsOpen(!isOpen);
        }}
        className={`multi-select__trigger${options.length === 0 ? ' multi-select__trigger--empty' : ''}`}
      >
        <span className={`multi-select__value${value.length === 0 ? ' multi-select__value--placeholder' : ''}`}>
          {getDisplayText()}
        </span>
      </button>

      {/* Действия лежат РЯДОМ с триггером, а не внутри него (#1341). Кнопка
          «очистить» внутри кнопки-триггера — невалидный HTML: браузер вправе
          выкинуть вложенную кнопку из внешней, а клик по ней проходил через
          onClick и onTouchEnd триггера, то есть очистка конкурировала с
          открытием списка.

          Слой абсолютный и прозрачный для мыши (`pointer-events: none`),
          перехватывает события только сама кнопка очистки. Поэтому клик по
          шеврону по-прежнему открывает список — он попадает в триггер под
          слоем. */}
      <div className="multi-select__actions">
        {value.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="multi-select__clear"
            aria-label="Clear selection"
          >
            <X className="multi-select__clear-icon" />
          </button>
        )}
        <ChevronDown
          className={`multi-select__chevron ui-chevron${isOpen ? ' ui-chevron-open' : ''}`}
          aria-hidden="true"
        />
      </div>

      {/* Dropdown with checkboxes - rendered via Portal to avoid overflow issues */}
      {/* Don't render dropdown when there are no options */}
      {isOpen && options.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          data-portal-dropdown="true"
          className="multi-select__dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            maxHeight: dropdownPosition.maxHeight,
            zIndex: 9999,
          }}
        >
          <div className="multi-select__list">
            {options.map((option) => (
              <label
                key={option}
                className="multi-select__option"
              >
                <input
                  type="checkbox"
                  checked={value.includes(option)}
                  onChange={() => toggleOption(option)}
                  className="multi-select__checkbox"
                />
                <span className="multi-select__option-label">{getOptionLabel(option)}</span>
                {counts && counts[option] != null && (
                  <span className="multi-select__option-count">{counts[option]}</span>
                )}
              </label>
            ))}
          </div>
        </div>,
        portalRoot
      )}
    </div>
  );
};

export default MultiSelect;
